export interface DayHours {
  open: string;
  close: string;
}

export type WeekHours = readonly (DayHours | null)[];

/**
 * A departure from the weekly pattern. Exactly one of the three forms:
 *
 *   { date: '2026-12-25' }                 one day
 *   { from: '2026-12-24', to: '2027-01-02' } every day in a range, inclusive
 *   { recurring: '12-25' }                 the same day every year
 *
 * `hours: null` means closed all day. Later entries win over earlier ones, so a
 * single-date exception can override a range it falls inside.
 */
export type HoursException = {
  hours: DayHours | null;
  note?: string;
} & ({ date: string } | { from: string; to: string } | { recurring: string });

export interface OpenState {
  open: boolean;
  minutesUntilClose: number | null;
  /** Open and close times of the session actually in progress, which may be yesterday's. */
  openedAt: string | null;
  closesAt: string | null;
  nextOpen: { day: string; time: string } | null;
  note: string | null;
  todayHours: DayHours | null;
}

export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const LONDON = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  weekday: 'short',
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function londonParts(date: Date) {
  const parts = LONDON.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const y = Number(get('year'));
  const m = Number(get('month'));
  const d = Number(get('day'));
  // Intl renders midnight as "24" in some en-GB configurations.
  const hh = Number(get('hour')) % 24;
  const mm = Number(get('minute'));
  const weekday = WEEKDAY_INDEX[get('weekday')] ?? 0;
  const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return { y, m, d, hh, mm, weekday, iso };
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

/**
 * "23:00" becomes "11pm", "16:30" becomes "4:30pm", "00:00" becomes "midnight".
 * Times are written the way a customer would say them, not the way the data
 * stores them.
 */
export function formatTime12(hhmm: string): string {
  const [rawHour, rawMinute] = hhmm.split(':');
  const hour24 = Number(rawHour);
  const minute = Number(rawMinute);
  if (minute === 0 && (hour24 === 0 || hour24 === 24)) return 'midnight';
  const suffix = hour24 % 24 < 12 ? 'am' : 'pm';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return minute === 0
    ? `${hour12}${suffix}`
    : `${hour12}:${String(minute).padStart(2, '0')}${suffix}`;
}

/**
 * A day's opening times as a span of minutes from that day's midnight.
 *
 * A close time at or before the open time means the session runs past midnight,
 * so it is expressed as minutes beyond 1440 rather than wrapping to a smaller
 * number. Without this, a shop closing at "00:00" would compare as closing at
 * minute zero and therefore never be open at all.
 */
function sessionMinutes(hours: DayHours): { start: number; end: number } {
  const start = toMinutes(hours.open);
  const rawEnd = toMinutes(hours.close);
  return { start, end: rawEnd <= start ? rawEnd + 1440 : rawEnd };
}

/** The exception in force on an ISO date, if any. Later entries take precedence. */
export function findException(
  exceptions: readonly HoursException[],
  iso: string,
): HoursException | undefined {
  const monthDay = iso.slice(5);
  let match: HoursException | undefined;
  for (const exception of exceptions) {
    if ('date' in exception && exception.date === iso) match = exception;
    else if ('recurring' in exception && exception.recurring === monthDay) match = exception;
    else if ('from' in exception && exception.from <= iso && iso <= exception.to) {
      match = exception;
    }
  }
  return match;
}

/** "4pm to midnight", or "Closed" for a day with no hours. */
export function formatDayHours(hours: DayHours | null): string {
  return hours ? `${formatTime12(hours.open)} to ${formatTime12(hours.close)}` : 'Closed';
}

function isoPlusDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const base = Date.UTC(y!, m! - 1, d!);
  const next = new Date(base + days * 86_400_000);
  return next.toISOString().slice(0, 10);
}

export function openState(
  now: Date,
  week: WeekHours,
  exceptions: readonly HoursException[],
): OpenState {
  const { weekday, hh, mm, iso } = londonParts(now);
  const exception = findException(exceptions, iso);
  const todayHours = exception ? exception.hours : (week[weekday] ?? null);
  const note = exception?.note ?? null;
  const nowMinutes = hh * 60 + mm;

  // Yesterday's session may still be running, if it closes after midnight.
  const yesterdayIso = isoPlusDays(iso, -1);
  const yesterdayException = findException(exceptions, yesterdayIso);
  const yesterdayHours = yesterdayException
    ? yesterdayException.hours
    : (week[(weekday + 6) % 7] ?? null);

  if (yesterdayHours) {
    const { end } = sessionMinutes(yesterdayHours);
    const nowInYesterdayFrame = nowMinutes + 1440;
    if (end > 1440 && nowInYesterdayFrame < end) {
      return {
        open: true,
        minutesUntilClose: end - nowInYesterdayFrame,
        openedAt: yesterdayHours.open,
        closesAt: yesterdayHours.close,
        nextOpen: null,
        note,
        todayHours,
      };
    }
  }

  if (todayHours) {
    const { start, end } = sessionMinutes(todayHours);
    if (nowMinutes >= start && nowMinutes < end) {
      return {
        open: true,
        minutesUntilClose: end - nowMinutes,
        openedAt: todayHours.open,
        closesAt: todayHours.close,
        nextOpen: null,
        note,
        todayHours,
      };
    }
    if (nowMinutes < start) {
      return {
        open: false,
        minutesUntilClose: null,
        openedAt: null,
        closesAt: null,
        nextOpen: { day: 'today', time: todayHours.open },
        note,
        todayHours,
      };
    }
  }

  // Closed for the rest of today — walk forward to the next open day.
  for (let step = 1; step <= 7; step += 1) {
    const dayIndex = (weekday + step) % 7;
    const isoAhead = isoPlusDays(iso, step);
    const ahead = findException(exceptions, isoAhead);
    const hours = ahead ? ahead.hours : (week[dayIndex] ?? null);
    if (hours) {
      const label = step === 1 ? 'tomorrow' : DAY_NAMES[dayIndex]!;
      return {
        open: false,
        minutesUntilClose: null,
        openedAt: null,
        closesAt: null,
        nextOpen: { day: label, time: hours.open },
        note,
        todayHours,
      };
    }
  }

  return {
    open: false,
    minutesUntilClose: null,
    openedAt: null,
    closesAt: null,
    nextOpen: null,
    note,
    todayHours,
  };
}

const HOUR_WORDS = [
  'twelve',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
];

/**
 * An opening time as a spoken phrase for display type: "four", "half past
 * four", "quarter to five". Anything else falls back to the clock form
 * ("4:20pm"), which is still true, just less pretty. Display type has no room
 * for "16:00", and "4pm" in a serif headline reads like a timetable.
 */
export function timeInWords(hhmm: string): string {
  const [rawHour, rawMinute] = hhmm.split(':');
  const hour24 = Number(rawHour) % 24;
  const minute = Number(rawMinute);
  const word = (h: number) => HOUR_WORDS[h % 12]!;
  if (minute === 0) return hour24 === 0 ? 'midnight' : word(hour24);
  if (minute === 30) return `half past ${word(hour24)}`;
  if (minute === 15) return `quarter past ${word(hour24)}`;
  if (minute === 45) return `quarter to ${word(hour24 + 1)}`;
  return formatTime12(hhmm);
}

/**
 * A one-line summary of the trading week, for the places that need a true
 * statement without reading the clock — the static render of the live status
 * indicator, and the page metadata.
 *
 * Derived from the data rather than written by hand, so changing WEEK_HOURS can
 * never leave a stale claim elsewhere in the site.
 */
export function summariseWeek(week: WeekHours): string {
  const first = week[0];
  const everyDaySame = week.every(
    (day) => day?.open === first?.open && day?.close === first?.close,
  );
  if (!first) return 'See opening hours';
  if (!everyDaySame) return 'See opening hours';
  return `${formatDayHours(first)} daily`;
}

/**
 * Exceptions falling within the next `days` days (tomorrow onwards), in date
 * order, so a page can warn about a closure before the day itself.
 */
export function upcomingExceptions(
  exceptions: readonly HoursException[],
  todayIso: string,
  days = 14,
): { iso: string; exception: HoursException }[] {
  const out: { iso: string; exception: HoursException }[] = [];
  for (let n = 1; n <= days; n += 1) {
    const iso = isoPlusDays(todayIso, n);
    const exception = findException(exceptions, iso);
    if (exception) out.push({ iso, exception });
  }
  return out;
}

/** "25 December", in British order, from an ISO date. */
export function formatDayMonth(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${iso}T12:00:00Z`));
}

/** "Seven days", "Six days a week", or "Two days a week". */
export function describeOpenDays(week: WeekHours): string {
  const words = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven'];
  const open = week.filter((day) => day !== null).length;
  return open === 7 ? 'Seven days' : `${words[open]} day${open === 1 ? '' : 's'} a week`;
}
